import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  FloorTypeForm,
  FloorTypeListMeta,
  FloorTypeRow,
  FloorTypeUpdatePayload,
} from "../types";
import { initialForm, initialState } from "./initialState";

export const sliceKey = "floorTypeState";

type FloorTypeListSuccessPayload = {
  data: FloorTypeRow[];
  meta: FloorTypeListMeta;
};

const slice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getListSuccess(state, action: PayloadAction<FloorTypeListSuccessPayload>) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<FloorTypeListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialForm;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof FloorTypeForm;
        value: string;
      }>,
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialForm;
    },

    add(state) {
      state.saving = true;
      state.error = null;
    },

    addSuccess(state) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialForm;
      state.error = null;
    },

    addError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    update(state, _action: PayloadAction<FloorTypeUpdatePayload>) {
      state.saving = true;
      state.error = null;
    },

    updateSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    delete(state, _action: PayloadAction<number>) {
      state.saving = true;
      state.error = null;
    },

    deleteSuccess(state, action: PayloadAction<number>) {
      state.saving = false;
      state.error = null;
      state.list = state.list.filter(
        (item) => item.floorTypeId !== action.payload,
      );
    },

    deleteError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions, reducer } = slice;
